import { ChevronDownIcon } from '@heroicons/react/outline';
import { FaSlack } from 'react-icons/fa6';

import styles from '../../agents.module.css';

import clsxm from '@/lib/clsxm';

import { useChannelsT } from '../useChannelsT';
import { delay, tone } from '../../primitives';

/**
 * Slack's install screen, where the workspace admits the app and picks the
 * channel it posts in, and the agent's list of connected workspaces.
 */
export default function SlackSetup({
  workspace,
  channel,
}: {
  workspace: string;
  channel: string;
}) {
  const { t } = useChannelsT();

  return (
    <div className='relative mx-auto max-w-[31rem] pb-2 sm:pb-24'>
      <div className='overflow-hidden rounded-[22px] bg-white px-6 py-6 text-start text-[#1d1c1d] shadow-[0_40px_90px_-35px_rgba(30,20,90,0.55)] ring-1 ring-black/5 dark:bg-[#1a1d21] dark:text-[#d1d2d3] dark:ring-white/10'>
        <div className='flex items-center justify-center gap-3'>
          <span className='flex h-11 w-11 items-center justify-center rounded-xl bg-[#f8f8f8] ring-1 ring-black/5 dark:bg-white'>
            <FaSlack className='h-6 w-6 text-[#4A154B]' />
          </span>
          <span className='flex gap-1' aria-hidden='true'>
            <span className='h-1 w-1 rounded-full bg-[#c4c4c4]' />
            <span className='h-1 w-1 rounded-full bg-[#c4c4c4]' />
            <span className='h-1 w-1 rounded-full bg-[#c4c4c4]' />
          </span>
          <span className='flex h-11 w-11 items-center justify-center rounded-xl bg-primary-600 text-[13px] font-bold text-white'>
            EP
          </span>
        </div>
        <p className='mt-4 text-center text-[15px] font-bold leading-snug dark:text-white'>
          {t(
            'Easy-Peasy.AI is requesting permission to access the {{workspace}} Slack workspace',
            { workspace },
          )}
        </p>
        <p className='mt-5 text-[13px] font-bold dark:text-white'>
          {t('Where should Easy-Peasy.AI post?')}
        </p>
        <span className='mt-2 flex items-center justify-between rounded-md px-3 py-2 text-[13px] ring-1 ring-inset ring-black/20 dark:ring-white/20'>
          <span dir='ltr'>#{channel}</span>
          <ChevronDownIcon className='h-4 w-4 text-[#616061]' />
        </span>
        <div className='mt-6 flex justify-end gap-2.5 text-[13px] font-bold'>
          <span className='rounded-md px-4 py-2 ring-1 ring-inset ring-black/20 dark:ring-white/20'>
            {t('Cancel')}
          </span>
          <span className='rounded-md bg-[#007a5a] px-4 py-2 text-white'>
            {t('Allow')}
          </span>
        </div>
      </div>

      <div
        className={clsxm(
          styles.rv,
          'relative mt-4 rounded-2xl p-4 text-start shadow-[0_30px_60px_-28px_rgba(30,20,90,0.6)] sm:absolute sm:-bottom-2 sm:end-[-1.5rem] sm:mt-0 sm:w-[20rem]',
          tone.surface,
          tone.line,
        )}
        style={delay(450)}
      >
        <p
          className={clsxm(
            'flex items-center gap-2 text-[13px] font-semibold',
            tone.ink,
          )}
        >
          <FaSlack className='h-4 w-4 text-[#4A154B] dark:text-white' />
          {t('Manage Slack workspaces')}
        </p>
        <div className='mt-3 flex items-center gap-3'>
          <span className='flex h-8 w-8 flex-none items-center justify-center rounded-lg bg-[#1264a3] text-[13px] font-bold text-white'>
            {workspace.charAt(0)}
          </span>
          <span className='min-w-0 flex-1'>
            <span
              className={clsxm(
                'block truncate text-[13px] font-semibold',
                tone.ink,
              )}
            >
              {workspace}
            </span>
            <span className={clsxm('block truncate text-xs', tone.quiet)}>
              <span dir='ltr'>#{channel}</span>
            </span>
          </span>
          <span className='mt-0.5 inline-flex flex-none rounded-full bg-emerald-50 px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-wide text-emerald-700 dark:bg-emerald-400/10 dark:text-emerald-300'>
            {t('Active')}
          </span>
          <span className='relative h-5 w-9 flex-none rounded-full bg-primary-600'>
            <span className='absolute end-0.5 top-0.5 h-4 w-4 rounded-full bg-white' />
          </span>
        </div>
      </div>
    </div>
  );
}
